import { GUIDE, type PageGuideStep } from './page-guide.model';

/**
 * Czech steps for each shell route. A step whose target is missing on the
 * open page is dropped when the guide starts.
 */
export function guideStepsForPath(url: string): PageGuideStep[] {
  const path = normalizeGuidePath(url);

  if (under(path, '/dashboard/gantt')) return GANTT;
  if (under(path, '/dashboard/finance')) return FINANCE;
  if (under(path, '/dashboard/material')) return DASH_MATERIAL;
  if (under(path, '/dashboard/kategorie')) return DASH_CATEGORY;
  if (path === '/dashboard') return DASHBOARD;

  if (path === '/maps' || under(path, '/maps')) return MAP;

  if (isIdPath(path, '/fakturace')) return FAKTURACE_DETAIL;
  if (path === '/fakturace') return FAKTURACE;

  if (isIdPath(path, '/fronta-prace')) return FRONTA_DETAIL;
  if (path === '/fronta-prace') return FRONTA;

  if (path === '/navolavani/kalendar') return NAVOLAVANI_CAL;
  if (path === '/navolavani/nastaveni') return NAVOLAVANI_SETTINGS;
  if (/^\/navolavani\/[^/]+\/item\//.test(path)) return NAVOLAVANI_ITEM;
  if (/^\/navolavani\/[^/]+$/.test(path)) return NAVOLAVANI_POLYGON;
  if (path === '/navolavani') return NAVOLAVANI;

  if (isIdPath(path, '/polygons')) return POLYGON_DETAIL;
  if (path === '/polygons') return POLYGONS;

  if (isIdPath(path, '/objects')) return OBJECT_DETAIL;
  if (path === '/objects') return OBJECTS;

  if (path === '/diary') return DIARY;
  if (isIdPath(path, '/nedodelky')) return NEDODELEK_DETAIL;
  if (path === '/nedodelky') return NEDODELKY;
  if (under(path, '/pd-changes')) return PD;

  if (path === '/urady/komunikace/k-schvaleni') return SCHVALENI;
  if (path === '/urady/komunikace/email' || under(path, '/urady/komunikace/email')) return EMAIL;
  if (path === '/urady/komunikace/datovka' || under(path, '/urady/komunikace/datovka')) return DATOVKA;
  if (under(path, '/urady/inzenyring')) return INZENYRING;
  if (under(path, '/urady/priprava')) return PRIPRAVA;
  if (under(path, '/urady')) return URADY_GENERIC;

  if (path === '/users/kontakty') return KONTAKTY;
  if (path === '/users/kalendar') return HR_CAL;
  if (/^\/users\/firmy\/[^/]+/.test(path)) return FIRMA_DETAIL;
  if (path === '/users/firmy') return FIRMY;
  if (/^\/users\/pozvanky\//.test(path)) return POZVANKA;
  if (/^\/users\/[^/]+/.test(path)) return USER_DETAIL;
  if (path === '/users') return USERS;

  if (under(path, '/work-report')) return VYKAZ;
  if (under(path, '/vydelek-firmy')) return VYDELEK;
  if (path === '/cenik' || under(path, '/cenik')) return CENIK;
  if (under(path, '/equipment')) return EQUIPMENT;
  if (under(path, '/materials')) return MATERIALS;
  if (under(path, '/import')) return IMPORT;
  if (under(path, '/admin/database')) return DATABASE;

  if (path === '/app-nastaveni/vzhled' || under(path, '/app-nastaveni/vzhled')) return VZHLED;
  if (under(path, '/app-nastaveni')) return PRIPOJENI;

  return [];
}

export function normalizeGuidePath(url: string): string {
  const raw = (url.split('?')[0] ?? '').split('#')[0] ?? '';
  if (raw.length > 1 && raw.endsWith('/')) return raw.slice(0, -1);
  return raw || '/';
}

function under(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

function isIdPath(path: string, prefix: string): boolean {
  return new RegExp(`^${prefix}/[^/]+`).test(path);
}

function heading(title: string, body: string): PageGuideStep {
  return { target: GUIDE.heading, title, body };
}
function actions(title: string, body: string): PageGuideStep {
  return { target: GUIDE.actions, title, body };
}
function filters(title: string, body: string): PageGuideStep {
  return { target: GUIDE.filters, title, body };
}
function table(title: string, body: string): PageGuideStep {
  return { target: GUIDE.table, title, body };
}
function detail(title: string, body: string): PageGuideStep {
  return { target: GUIDE.detail, title, body };
}
function search(body: string): PageGuideStep {
  return { target: GUIDE.listSearch, title: 'Hledání', body };
}
function exp(body: string): PageGuideStep {
  return { target: GUIDE.listExport, title: 'Export', body };
}
function pages(
  body = 'Šipky a číslo stránky posouvají řádky. Zobrazit vše načte celý výběr najednou.',
): PageGuideStep {
  return { target: GUIDE.listPages, title: 'Stránky', body };
}

const DASHBOARD: PageGuideStep[] = [
  heading('Přehled', 'Čísla portfolia na jeden pohled. Nadpis potvrzuje, že jste na přehledu.'),
  { target: GUIDE.dashPolygon, title: 'Polygon', body: 'Ikona polygonu nechá v kartách jen vybrané stavby. Prázdný výběr znamená všechny polygony.' },
  { target: GUIDE.dashBranch, title: 'Pobočka', body: 'Ikona domu omezí karty na zodpovědnou pobočku. Prázdný výběr bere všechny pobočky.' },
  exp('Ikona šipky stáhne sešit s čísly, která teď karty ukazují.'),
  { target: GUIDE.kpi, title: 'Karty ukazatelů', body: 'Každá karta je jedna oblast. Klepnutím otevřete její vývoj v čase.' },
  { target: GUIDE.kpiChart, title: 'Růst v čase', body: 'Graf ukazuje, jak se čísla měnila. V bodě uvidíte hodnotu za dané období.' },
];

const DASH_CATEGORY: PageGuideStep[] = [
  heading('Kategorie', 'Nadpis je oblast, kterou jste otevřeli z přehledu.'),
  filters('Období a stavby', 'Rozlišení a rozsah dat mění křivku. Polygon a pobočka drží výběr z přehledu.'),
  { target: GUIDE.kpi, title: 'Karty', body: 'Zvýrazněná karta je ta, kterou sledujete. Jiná karta přepne oblast.' },
  { target: GUIDE.kpiChart, title: 'Růst v čase', body: 'Graf ukazuje, jak se čísla měnila. V bodě uvidíte hodnotu za dané období.' },
];

const GANTT: PageGuideStep[] = [
  heading('Gantt', 'Harmonogram staveb. Nadpis potvrzuje pohled Gantt.'),
  search('Lupa otevře pole. Hledá vedoucího stavby, název, KIA, složku, stav a pobočku.'),
  { target: GUIDE.ganttYear, title: 'Rok realizace', body: 'Ikona kalendáře nechá jen stavby zvoleného roku. Prázdný výběr znamená všechny roky.' },
  { target: GUIDE.ganttBranch, title: 'Pobočka', body: 'Ikona domu filtruje podle zodpovědné pobočky.' },
  { target: GUIDE.ganttType, title: 'Způsob stavby', body: 'Ikona stavby nechá jen vybraný způsob. Prázdný výběr ukáže všechny typy.' },
  { target: GUIDE.ganttStatus, title: 'Stav', body: 'Ikona stavu nechá na ose jen polygony ve vybraných stavech: Nezařazené, Projekce, Příprava, Stavba, Archiv. Prázdný výběr znamená všechny.' },
  exp('Ikona šipky stáhne řádky, které jsou teď na ose.'),
  { target: GUIDE.gantt, title: 'Časová osa', body: 'Řádek je stavba, pruh je plán. Posunem do stran jedete v čase, klepnutím na pruh plán upravíte.' },
  { target: GUIDE.ganttToday, title: 'Dnes', body: 'Tenká šedá čára je dnešek. U ní v hlavičce je datum.' },
];

const FINANCE: PageGuideStep[] = [
  heading('Finance', 'Souhrn práce, materiálu a výsledku staveb.'),
  { target: GUIDE.financeDate, title: 'Skutečnost k datu', body: 'Kalendář určí den, ke kterému se náklady a výnosy počítají.' },
  { target: GUIDE.financePolygon, title: 'Polygon', body: 'Ikona polygonu vybere stavby, které se sčítají. Prázdný výběr bere všechny.' },
  { target: GUIDE.financeStatus, title: 'Stav plánu', body: 'Přepínač stavu nechá ve výpočtu jen stavby v daném stavu plánu.' },
  exp('Ikona šipky stáhne sešit souhrnu pro dnešní výběr.'),
  { target: GUIDE.finance, title: 'Souhrn', body: 'Karty sečtou náklady a výnosy. Klepnutí na práci nebo materiál otevře podklad.' },
  { target: GUIDE.financePlan, title: 'Plán a skutečnost', body: 'Tabulka srovná plán PPD a skutečnost. Řádek se šipkou rozbalí skupinu, klepnutí otevře detail.' },
];

const DASH_MATERIAL: PageGuideStep[] = [
  heading('Materiál', 'Hodnota zásob, příjem a výdej, nebo položky pod minimem. Pohled přepnete v menu.'),
  filters('Sklad', 'Ikona skladu, a u příjmu i datum, přepočítá graf i tabulku.'),
  exp('Ikona šipky stáhne čísla otevřeného pohledu.'),
  { target: GUIDE.dashChart, title: 'Graf', body: 'Graf rozloží hodnotu skladů nebo tok příjmu a výdeje za zvolené období.' },
  table('Tabulka', 'Řádky jsou kategorie nebo položky. Čísla sedí k filtru nahoře.'),
];

const MAP: PageGuideStep[] = [
  {
    target: GUIDE.mapDrawing,
    title: 'Výkres',
    body: 'Přepne sadu objektů a jejich barvy. Stavba, Montáž, Záfuk a Optika drží typy dané práce, Obecný ukáže všechny. Optika výkopové čáry nekreslí.',
  },
  {
    target: GUIDE.mapObjects,
    title: 'Objekty',
    body: 'Čtverec je budova, kolečko rozvaděč, spojkoviště nebo info, kroužek montážní jáma — to jsou body. Čára je Výkop nebo Stávající trasa. Ve skupině Typ trasy jsou R100 (import), R100 (dokreslené), MET a UR. Vypnutý typ z mapy zmizí.',
  },
  {
    target: GUIDE.mapLinks,
    title: 'Návaznosti',
    body: 'Čárkované spoje mezi objekty: optické kabely, MT a plánované. Zapnutá kategorie se na mapě vykreslí, vypnutá zmizí.',
  },
  {
    target: GUIDE.mapLegend,
    title: 'Barvy',
    body: 'Tlačítko vpravo dole rozbalí barvy vybraného výkresu. U výkopu je Nevykopaný červený, Vykopaný oranžový a hotové fáze Finální a Předaný žluté nebo zelené. Tvar vzorku říká, jestli jde o čáru, čtverec, nebo kolečko.',
  },
  {
    target: GUIDE.mapCanvas,
    title: 'Klepnutí na objekt',
    body: 'Bod je čtverec (budova) nebo kolečko (spojkoviště, rozvaděč, INFO). Klepnutí na něj otevře kartu. Čára je trasa výkopu — tu průvodce vybere až po tlačítkách karty.',
  },
  {
    target: GUIDE.mapPopupHeader,
    title: 'Karta bodu',
    body: 'Průvodce otevřel bod na mapě. V hlavičce je Smazat, Přesunout, případně Název, Informace a Zavřít. Další kroky vezmou tlačítka, která tahle karta má.',
  },
  {
    target: GUIDE.mapVykopDraw,
    title: 'Kreslení výkopu',
    body: 'Každé klepnutí do mapy přidá bod čáry. Zpět ubere poslední bod. Vytvořit výkop čáru uloží. Zrušit kreslení ukončí.',
  },
  {
    target: GUIDE.mapPointType,
    title: 'Typ budovy',
    body: 'Připojená a Bábovka zvolí typ a otevřou milníky. Nerealizované budovu ukončí.',
  },
  {
    target: GUIDE.mapPointMilestones,
    title: 'Milníky bodu',
    body: 'Každé tlačítko je milník budovy: Zavrtaný, Zřízené VR, Odbočeno, Připravená bábovka nebo Zafouknutý, podle typu. Klepnutí milník dokončí. Šipka zpět u hotového milníku ho vrátí. Změnit typ budovy vrátí výběr typu.',
  },
  {
    target: GUIDE.mapPointSpec,
    title: 'Specifikace milníku',
    body: 'Specifikovat závrt, VR, odbočku nebo bábovku doplní parametry milníku. Zadat záfuk zapíše záfuk. Tlačítko patří k otevřenému milníku.',
  },
  {
    target: GUIDE.mapRozvadecMilestones,
    title: 'Milníky rozvaděče',
    body: 'Provaření otevře svařování rozvaděče. Šipka zpět u hotového milníku ho vrátí. Tlačítko s názvem předchozí fáze couvá ve workflow.',
  },
  {
    target: GUIDE.mapRozvadecSpec,
    title: 'Provaření',
    body: 'Specifikovat provaření doplní svary. Schéma osazení (D5) otevře osazení rozvaděče.',
  },
  {
    target: GUIDE.mapBtnDelete,
    title: 'Smazat',
    body: 'Koš smaže bodový objekt. U trasy smaže vybrané úseky.',
  },
  {
    target: GUIDE.mapBtnMove,
    title: 'Přesunout',
    body: 'Čtyři šipky přesunou bod na nové místo na mapě. Čáru výkopu takhle nepřesunete.',
  },
  {
    target: GUIDE.mapBtnRename,
    title: 'Název rozvaděče',
    body: 'Tužka otevře úpravu názvu rozvaděče. Uložit název zapíše, Zrušit ho zahodí.',
  },
  {
    target: GUIDE.mapBtnInfo,
    title: 'Informace',
    body: 'Otevře detail objektu: fáze, adresa a u budovy i smlouvy.',
  },
  {
    target: GUIDE.mapBtnClose,
    title: 'Zavřít kartu',
    body: 'Křížek kartu zavře a vrátí vás na mapu.',
  },
  {
    target: GUIDE.mapBtnPhotos,
    title: 'Fotky',
    body: 'Otevře galerii fotek objektu.',
  },
  {
    target: GUIDE.mapBtnMaterial,
    title: 'Použitý materiál',
    body: 'Otevře seznam materiálu použitého na objektu.',
  },
  {
    target: GUIDE.mapBtnNote,
    title: 'Poznámka',
    body: 'Otevře textovou poznámku k objektu.',
  },
  {
    target: GUIDE.mapBtnShare,
    title: 'Spolupráce s',
    body: 'Zapíše, kdo na objektu pracoval s vámi.',
  },
  {
    target: GUIDE.mapBtnSurfaces,
    title: 'Povrchy',
    body: 'Zadá výkop a obnovu: délku, povrch a rozměry. U výkopu je to zápis práce na čáře.',
  },
  {
    target: GUIDE.mapBtnWelds,
    title: 'Počet svarů',
    body: 'Doplní počet svarů u provaření rozvaděče.',
  },
  {
    target: GUIDE.mapBtnSpec,
    title: 'Specifikovat',
    body: 'Doplní parametry aktuální fáze, třeba závrt, VR nebo záfuk.',
  },
  {
    target: GUIDE.mapPopupPhases,
    title: 'Posun fáze',
    body: 'Tlačítko se šipkou vpravo posune objekt do fáze, jejíž název je na tlačítku. Šipka vlevo vrátí předchozí fázi. Dokončeno znamená konečnou fázi.',
  },
  {
    target: GUIDE.mapPopupAcq,
    title: 'Akvizice',
    body: 'Podepsat uzavře souhlas. Nespecifikováno nechá stav bez rozhodnutí. Zamítnuto souhlas odmítne.',
  },
  {
    target: GUIDE.mapLineRoute,
    title: 'Výběr trasy',
    body: 'Čára výkopu má Zvolit trasu (poslední úsek, jen když existuje) a Vybrat novou (nový výběr klepáním). Fáze čáry je tlačítko s jejím názvem, když ho karta ukáže. Klepnutí by fázi zapsalo — průvodce ho nespouští.',
  },
  {
    target: GUIDE.mapRouteChoose,
    title: 'Zvolit trasu',
    body: 'Vezme poslední uloženou dávku úseků. Tlačítko je tu jen tehdy, když taková dávka existuje.',
  },
  {
    target: GUIDE.mapRouteNew,
    title: 'Vybrat novou',
    body: 'Spustí výběr úseků na čáře. Dole na mapě pak přibude Potvrdit výběr a Zrušit.',
  },
  {
    target: GUIDE.mapLinePhase,
    title: 'Fáze čáry',
    body: 'Tlačítko nese název další fáze, typicky Vykopaný nebo Finální. Klepnutí by fázi na vybranou trasu zapsalo. Průvodce ho nespouští.',
  },
  {
    target: GUIDE.mapVykopConfirm,
    title: 'Lišta výběru',
    body: 'Potvrdit výběr by úseky uložil. Vrátit úseky je totéž tlačítko, když výběr couvá. Zrušit režim ukončí. Průvodce nic neukládá.',
  },
  {
    target: GUIDE.mapRouteConfirm,
    title: 'Potvrdit výběr',
    body: 'Uloží naklikané úseky. Ve vracení se jmenuje Vrátit úseky. Průvodce ho nespouští.',
  },
  {
    target: GUIDE.mapRouteCancel,
    title: 'Zrušit výběr',
    body: 'Ukončí výběr trasy a nic neuloží. Průvodce tím výběr zavře.',
  },
];

const FAKTURACE: PageGuideStep[] = [
  heading('Fakturace', 'Seznam faktur a podkladů k vyúčtování.'),
  { target: GUIDE.invoiceSummary, title: 'Stavy', body: 'Karty jsou stavy faktur. Klepnutím necháte v tabulce jen jeden stav.' },
  search('Lupa otevře pole. Hledá číslo faktury, vlastníka a kdo ji vystavil.'),
  { target: 'invoice-dates', title: 'Období', body: 'Kalendář nastaví od a do. Mimo rozsah faktury v tabulce nejsou.' },
  { target: 'invoice-user', title: 'Uživatel', body: 'Ikona lidí nechá jen faktury vybraného uživatele. Prázdný výběr znamená všechny.' },
  exp('Ikona šipky stáhne řádky, které jsou teď v tabulce.'),
  table('Faktury', 'Řádek otevře detail s částkou a položkami.'),
  { target: GUIDE.invoiceRow, title: 'Akce řádku', body: 'Oko otevře detail. Fajfka označí Proplaceno. Šipka vlevo je Vrátit. Křížek je Zrušit. Vidíte jen akce, které stav faktury dovolí.' },
  pages(),
];

const FAKTURACE_DETAIL: PageGuideStep[] = [
  heading('Detail faktury', 'Nadpis označuje fakturu. Podnadpis říká, že jde o řádky a částku.'),
  { target: GUIDE.invoicePay, title: 'Proplatit', body: 'Zapíše fakturu jako proplacenou. Tlačítko je tu, jen dokud ještě proplacená není.' },
  { target: GUIDE.invoiceRevert, title: 'Vrátit', body: 'Vrátí proplacenou fakturu zpět mezi neproplacené.' },
  { target: GUIDE.invoiceDelete, title: 'Odstranit', body: 'Koš a nápis Odstranit fakturu smažou. Potvrzení se zeptá znovu.' },
  detail('Souhrn', 'Blok pod nadpisem ukáže, kdo fakturu vystavil, stav a částku vpravo.'),
  table('Řádky', 'Tabulka jsou položky faktury. Sčítají se do částky nahoře.'),
];

const FRONTA: PageGuideStep[] = [
  heading('Fronta práce', 'Stavby seřazené podle toho, co je potřeba udělat.'),
  search('Lupa otevře pole a nechá ve frontě jen stavby, jejichž text sedí.'),
  filters('Filtry', 'Ikony vedle lupy nechají jen vybrané stavby nebo stavy.'),
  exp('Ikona šipky stáhne řádky, které jsou teď ve frontě.'),
  table('Fronta', 'Řádek je stavba. Klepnutím otevřete její frontu a konkrétní práci.'),
  pages(),
];

const FRONTA_DETAIL: PageGuideStep[] = [
  heading('Práce na stavbě', 'Nadpis je stavba, jejíž frontu máte otevřenou.'),
  search('Lupa zužuje úkoly na této stavbě.'),
  filters('Filtr práce', 'Ikony vedle lupy nechají jen vybraný stav nebo typ práce.'),
  exp('Ikona šipky stáhne úkoly, které jsou teď v tabulce.'),
  table('Úkoly', 'Řádek je práce k odbavení. Klepnutím otevřete detail nebo objekt.'),
  pages(),
  detail('Detail úkolu', 'Blok s údaji k vybrané práci, pokud ho tahle obrazovka ukazuje.'),
];

const NAVOLAVANI: PageGuideStep[] = [
  heading('Navolávání', 'Počty kontaktů k navolání podle druhu práce na stavbách.'),
  search('Lupa najde polygon podle názvu nebo KIA.'),
  filters('Filtry', 'Ikony vedle lupy zužují seznam staveb.'),
  exp('Ikona šipky stáhne stavby, které jsou teď v tabulce.'),
  table('Stavby', 'Řádek otevře seznam míst, která se mají navolat.'),
  pages(),
];

const NAVOLAVANI_POLYGON: PageGuideStep[] = [
  heading('Objekty k navolání', 'Nadpis je stavba. Seznam jsou místa, kde je potřeba domluvit termín.'),
  search('Lupa zužuje objekty na této stavbě.'),
  filters('Filtry', 'Ikony vedle lupy nechají jen vybraný stav nebo typ.'),
  exp('Ikona šipky stáhne místa, která jsou teď v tabulce.'),
  table('Místa', 'Řádek otevře kontakt, termín a stav navolání.'),
  pages(),
];

const NAVOLAVANI_ITEM: PageGuideStep[] = [
  heading('Kontakt a termín', 'Detail jednoho místa: komu volat a na kdy je závrt.'),
  actions('Úprava a mapa', 'Tužka upraví navolání. Mapa ukáže místo, „Otevřít budovu“ přejde na objekt.'),
  detail('Údaje', 'Bloky pod nadpisem jsou kontakt, stav a domluvený termín.'),
];

const NAVOLAVANI_CAL: PageGuideStep[] = [
  heading('Kalendář techniků', 'Termíny závrtů a výjezdů.'),
  actions('Období', 'Přepínač dne, týdne a měsíce a šipky posouvají zobrazené období. Dnes skočí na aktuální den.'),
  search('Lupa nechá v mřížce jen termíny, jejichž text sedí.'),
  filters('Polygon', 'Ikona vedle lupy nechá jen termíny vybraného polygonu.'),
  { target: GUIDE.calendarPeople, title: 'Kalendáře', body: 'Každý čip je člověk nebo kalendář. Vypnutý čip jeho termíny schová.' },
  { target: GUIDE.calendar, title: 'Mřížka', body: 'Den ukáže termíny. Klepnutím otevřete detail dne.' },
];

const NAVOLAVANI_SETTINGS: PageGuideStep[] = [
  heading('Kalendáře', 'Napojení termínů firmy pro navolávání.'),
  detail('Nastavení', 'Tady volíte, které kalendáře se do navolávání berou.'),
  search('Lupa zužuje seznam kalendářů, pokud ho obrazovka má.'),
  table('Seznam', 'Řádky jsou kalendáře nebo pravidla, pokud je obrazovka vypisuje.'),
  pages(),
];

const POLYGONS: PageGuideStep[] = [
  heading('Polygony', 'Seznam staveb.'),
  search('Lupa hledá název, KIA, vedoucího stavby, přípraváře, projektanta a pobočku.'),
  { target: 'polygon-year', title: 'Rok realizace', body: 'Ikona kalendáře nechá jen stavby zvoleného roku. Prázdný výběr znamená všechny roky.' },
  { target: 'polygon-branch', title: 'Pobočka', body: 'Ikona domu filtruje podle zodpovědné pobočky.' },
  { target: 'polygon-type', title: 'Typ stavby', body: 'Ikona stavby nechá jen vybraný typ. Prázdný výběr ukáže všechny typy.' },
  { target: 'polygon-clear', title: 'Zrušit filtry', body: 'Tlačítko smaže rok, pobočku i typ a vrátí celý seznam. Je vidět, jen když je nějaký filtr zapnutý.' },
  exp('Ikona šipky stáhne stavby, které jsou teď v tabulce.'),
  table('Seznam', 'Řádek otevře přehled stavby. Další záložky jsou v menu vlevo.'),
  pages(),
];

const POLYGON_DETAIL: PageGuideStep[] = [
  heading('Stavba', 'Nadpis je název stavby. Záložky v menu přepínají přehled, deník, materiál a další.'),
  actions('Akce stavby', 'Tlačítka vpravo mění záložka: export, mapa nebo specifikace.'),
  search('Lupa zužuje řádky otevřené záložky.'),
  filters('Filtr záložky', 'Ikony vedle lupy patří k otevřené záložce.'),
  exp('Ikona šipky stáhne řádky, které záložka právě ukazuje.'),
  detail('Obsah záložky', 'Čísla, graf nebo údaje k této stavbě.'),
  table('Tabulka záložky', 'Řádky patří k otevřené záložce, třeba výkaz nebo dokumenty.'),
  pages(),
];

const OBJECTS: PageGuideStep[] = [
  heading('Objekty', 'Body sítě: rozvaděče, budovy a trasy.'),
  search('Lupa najde objekt podle označení nebo adresy.'),
  filters('Typ a fáze', 'Ikony vedle lupy nechají jen vybraný typ objektu nebo fázi.'),
  exp('Ikona šipky stáhne objekty, které jsou teď v tabulce.'),
  table('Seznam', 'Řádek otevře detail objektu.'),
  pages(),
];

const OBJECT_DETAIL: PageGuideStep[] = [
  heading('Objekt', 'Nadpis je označení objektu. Podnadpis říká, že jde o jeho údaje.'),
  { target: GUIDE.objectMap, title: 'Zobrazit na mapě', body: 'Otevře tenhle objekt na mapě.' },
  actions('Smazat', 'Koš vedle mapy objekt smaže, pokud na to máte právo.'),
  { target: GUIDE.objectPhase, title: 'Fáze', body: 'Tužka u identity otevře úpravu fáze a milníků. V bloku je typ, fáze a milníky.' },
  { target: GUIDE.objectSpec, title: 'Specifikace', body: 'U rozvaděče je specifikace záfuku a jména, u budovy specifikace VR. Tužka vpravo otevře úpravu.' },
  { target: GUIDE.objectPhotos, title: 'Fotky', body: 'Fotky jsou po fázích. Klepnutím na náhled ji zvětšíte. Stáhnout a smazat jsou u karty, pokud je smíte.' },
  detail('Údaje', 'Další bloky pod nadpisem: povrchy výkopu, materiál a historie, podle typu objektu.'),
];

const DIARY: PageGuideStep[] = [
  heading('Stavební deník', 'Záznamy z průběhu staveb. Rozsah přepnete v menu.'),
  search('Lupa nechá jen záznamy, jejichž text sedí.'),
  filters('Filtry', 'Ikony vedle lupy zužují deník podle stavby nebo období.'),
  exp('Ikona šipky stáhne záznamy, které jsou teď v tabulce.'),
  table('Záznamy', 'Řádek je záznam deníku. Klepnutím ho otevřete.'),
  pages(),
];

const NEDODELKY: PageGuideStep[] = [
  heading('Nedodělky', 'Práce, která na stavbě ještě zbývá: fotka, poznámka, přiřazení.'),
  actions('Nový nedodělek', 'Plus založí nový nedodělek.'),
  search('Lupa najde nedodělek podle textu.'),
  filters('Filtry', 'Ikony vedle lupy zužují seznam podle stavby, stavu nebo člověka.'),
  exp('Ikona šipky stáhne nedodělky, které jsou teď v tabulce.'),
  table('Seznam', 'Řádek otevře detail nedodělku.'),
  pages(),
];

const NEDODELEK_DETAIL: PageGuideStep[] = [
  heading('Nedodělek', 'Nadpis je konkrétní nedodělek a jeho stav.'),
  actions('Mapa', 'Tlačítko u nadpisu ukáže místo na mapě.'),
  detail('Popis', 'Fotka, poznámka a přiřazení jsou v blocích pod nadpisem.'),
  table('Související', 'Tabulka, pokud je u nedodělku seznam návazných položek.'),
];

const PD: PageGuideStep[] = [
  heading('Změny oproti PD', 'Kde se skutečnost liší od projektu: poznámky, HP, odbočky, návaznost.'),
  search('Lupa najde stavbu nebo text odchylky.'),
  filters('Filtry', 'Ikony vedle lupy zužují stavby nebo řádky odchylek.'),
  exp('Ikona šipky stáhne řádky, které jsou teď v tabulce.'),
  table('Odchylky', 'Řádek otevře stavbu nebo konkrétní změnu.'),
  pages(),
  detail('Detail změny', 'Blok s popisem odchylky, pokud jste v detailu.'),
];

const INZENYRING: PageGuideStep[] = [
  heading('Inženýring', 'Věcná břemena, dotčené orgány a průběh staveb.'),
  actions('Nový záznam', 'Plus přidá dokument nebo položku, pokud to tahle záložka umí.'),
  search('Lupa zužuje stavby, orgány nebo případy.'),
  filters('Filtry', 'Ikony vedle lupy nechají jen vybraný stav nebo typ.'),
  exp('Ikona šipky stáhne řádky otevřené záložky.'),
  table('Seznam', 'Řádek otevře stavbu, případ nebo smlouvu.'),
  pages(),
  detail('Detail', 'Údaje otevřeného případu nebo smlouvy.'),
];

const PRIPRAVA: PageGuideStep[] = [
  heading('Příprava', 'Křížení sítí, vytyčení a povinnosti před realizací.'),
  actions('Nový záznam', 'Plus přidá dokument nebo povinnost, pokud to záložka nabízí.'),
  search('Lupa zužuje stavby a položky přípravy.'),
  filters('Filtry', 'Ikony vedle lupy nechají jen vybranou stavbu nebo stav.'),
  exp('Ikona šipky stáhne řádky otevřené záložky.'),
  table('Seznam', 'Řádek otevře stavbu, křížení, vytyčení nebo povinnost.'),
  pages(),
  detail('Detail', 'Údaje otevřené položky přípravy.'),
];

const EMAIL: PageGuideStep[] = [
  heading('Email', 'Schránka Gmail napojená na stavby.'),
  actions('Nová zpráva', 'Tlačítko u nadpisu založí zprávu, pokud je schránka připojená.'),
  { target: GUIDE.mailFolders, title: 'Složky', body: 'Doručené, odeslané a skupiny. Klepnutím přepnete, které zprávy jsou v seznamu.' },
  search('Lupa hledá v předmětu a textu zpráv.'),
  filters('Filtry', 'Ikony vedle lupy zužují seznam zpráv.'),
  table('Zprávy', 'Řádek otevře zprávu, přílohy a přiřazení ke stavbě.'),
  pages(),
];

const DATOVKA: PageGuideStep[] = [
  heading('Datovka', 'Datové schránky a přijaté zprávy úřadů.'),
  actions('Nová zpráva', 'Tlačítko u nadpisu založí datovou zprávu, pokud je schránka připojená.'),
  { target: GUIDE.mailFolders, title: 'Složky', body: 'Složky schránky a skupiny. Klepnutím přepnete obsah seznamu.' },
  search('Lupa hledá v předmětu a odesílateli datové zprávy.'),
  filters('Filtry', 'Ikony vedle lupy zužují seznam datových zpráv.'),
  table('Zprávy', 'Řádek otevře zprávu, přílohy a přiřazení ke stavbě.'),
  pages(),
];

const SCHVALENI: PageGuideStep[] = [
  heading('Ke schválení', 'Zprávy, které čekají na rozhodnutí, než se zapíšou ke stavbě.'),
  search('Lupa zužuje frontu ke schválení.'),
  filters('Filtry', 'Ikony vedle lupy nechají jen vybraný typ nebo stav.'),
  table('Fronta', 'Řádek otevře návrh. Schválíte ho, nebo vrátíte k úpravě.'),
  pages(),
];

const URADY_GENERIC: PageGuideStep[] = [
  heading('Úřady', 'Příprava nebo inženýring — nadpis říká, kterou agendu máte otevřenou.'),
  actions('Akce', 'Tlačítka u nadpisu přidají záznam, pokud ho tahle obrazovka nabízí.'),
  search('Lupa zužuje seznam.'),
  filters('Filtry', 'Ikony vedle lupy nechají jen vybrané položky.'),
  exp('Ikona šipky stáhne řádky, které jsou teď v tabulce.'),
  table('Seznam', 'Řádek otevře detail položky.'),
  pages(),
  detail('Detail', 'Údaje otevřeného záznamu.'),
];

const USERS: PageGuideStep[] = [
  heading('Uživatelé', 'Lidé, kteří se do TelcoTracku hlásí. Pozvánky jsou záložka v menu.'),
  actions('Pozvánka', 'Plus vpravo u nadpisu otevře pozvánku novému uživateli.'),
  search('Lupa hledá příjmení, jméno a e-mail. Na záložce Pozvánky stejné pole hledá v pozvánkách.'),
  { target: GUIDE.usersBranch, title: 'Pobočka', body: 'Ikona domu nechá jen lidi vybrané pobočky. Prázdný výběr znamená všechny pobočky.' },
  { target: GUIDE.usersStatus, title: 'Stav', body: 'Ikona stavu nechá jen jeden stav uživatele, třeba aktivní. Prázdný výběr ukáže všechny stavy.' },
  exp('Ikona šipky stáhne lidi, kteří jsou teď v tabulce.'),
  table('Adresář', 'Řádek otevře kartu uživatele. Tužka na kartě otevře role a mřížku oprávnění.'),
  pages(),
];

const KONTAKTY: PageGuideStep[] = [
  heading('Kontakty', 'Adresář lidí mimo přihlášení do aplikace.'),
  search('Lupa najde kontakt podle jména, firmy nebo telefonu.'),
  filters('Filtry', 'Ikony vedle lupy zužují adresář, pokud je obrazovka má.'),
  exp('Ikona šipky stáhne kontakty, které jsou teď v tabulce.'),
  table('Kontakty', 'Řádek ukáže údaje kontaktu. Tužka u řádku otevře úpravu, pokud ji smíte.'),
  pages(),
];

const FIRMY: PageGuideStep[] = [
  heading('Firmy', 'Firmy, jejich lidé a smluvní ceník.'),
  actions('Nová firma', 'Plus založí firmu, pokud na to máte právo.'),
  search('Lupa najde firmu podle názvu.'),
  filters('Filtry', 'Ikony vedle lupy zužují seznam firem.'),
  exp('Ikona šipky stáhne firmy, které jsou teď v tabulce.'),
  table('Seznam', 'Řádek otevře firmu. Záložky v menu přepínají přehled, uživatele a ceník.'),
  pages(),
];

const FIRMA_DETAIL: PageGuideStep[] = [
  heading('Firma', 'Nadpis je název firmy. Záložky v menu přepínají přehled, uživatele a ceník.'),
  actions('Lidé a úprava', 'Přiřazení, pozvánka nebo tužka podle otevřené záložky.'),
  search('Lupa zužuje uživatele nebo položky ceníku.'),
  filters('Filtr záložky', 'Ikony vedle lupy patří k otevřené záložce.'),
  exp('Ikona šipky stáhne řádky otevřené záložky.'),
  detail('Údaje firmy', 'Přehled firmy: identifikace a nastavení.'),
  table('Tabulka záložky', 'Uživatelé nebo řádky smluvního ceníku.'),
  pages(),
];

const HR_CAL: PageGuideStep[] = [
  heading('Kalendář kontrol', 'Lékařské a další kontroly zaměstnanců.'),
  actions('Období', 'Den, týden nebo měsíc a šipky posouvají kalendář. Dnes skočí na aktuální den.'),
  search('Lupa najde člověka nebo typ kontroly.'),
  filters('Filtry', 'Ikony vedle lupy zužují, které kontroly jsou v mřížce.'),
  { target: GUIDE.calendarPeople, title: 'Zaměstnanci', body: 'Čip zapne nebo vypne kontroly daného člověka. Všichni je vrátí najednou.' },
  { target: GUIDE.calendar, title: 'Dny', body: 'Klepnutí na den otevře seznam kontrol. Klepnutí na kontrolu ukáže její detail.' },
];

const USER_DETAIL: PageGuideStep[] = [
  heading('Uživatel', 'Nadpis je jméno člověka. Záložky v menu přepínají profil, výkaz a další.'),
  actions('Akce profilu', 'Vpravo u nadpisu je složka Drive, export, ceník, tužka nebo koš — podle záložky a práva.'),
  { target: GUIDE.userEdit, title: 'Upravit účet', body: 'Tužka otevře okno účtu. Čipy rolí jen předvyplní zaškrtnutí. Oprávnění se ukládají z mřížky, roli vybírat nemusíte.' },
  { target: GUIDE.userPermissions, title: 'Oprávnění', body: 'Mřížka modulů. Zaškrtnutí zapíše, co účet smí. Role nahoře jen předvyplní, uložení bere mřížku.' },
  search('Lupa zužuje výkaz nebo aktivitu na otevřené záložce.'),
  filters('Filtr období', 'Datum a ikony vedle lupy zužují výkaz nebo aktivitu.'),
  exp('Ikona šipky stáhne řádky otevřené záložky.'),
  detail('Profil', 'Údaje člověka, role a kontakty.'),
  table('Tabulka záložky', 'Řádky výkazu nebo aktivity za zvolené období. Tužka u řádku výkaz upraví.'),
  pages(),
];

const POZVANKA: PageGuideStep[] = [
  heading('Pozvánka', 'Stav jedné odeslané pozvánky.'),
  actions('Akce pozvánky', 'Tlačítko u nadpisu pozvánku odebere nebo zopakuje, pokud je to ještě možné.'),
  detail('Údaje', 'Komu pozvánka patří a v jakém je stavu.'),
];

const VYKAZ: PageGuideStep[] = [
  heading('Výkaz práce', 'Odvedená práce za zvolené období.'),
  { target: GUIDE.wrAdd, title: 'Přidat záznam', body: 'Plus vpravo u nadpisu založí nový řádek výkazu.' },
  { target: GUIDE.wrBilling, title: 'Platby', body: 'Označit jako vyfakturované zapíše výběr. Nastavit jako neproplacené ho vrátí. Vedle je počet vybraných řádků.' },
  search('Lupa hledá název činnosti, adresu a polygon.'),
  { target: GUIDE.wrDates, title: 'Období', body: 'Kalendář nastaví od a do. Mimo ten rozsah řádky v tabulce nejsou.' },
  { target: GUIDE.wrUser, title: 'Uživatel', body: 'Ikona lidí přepne, čí výkaz vidíte. Prázdný výběr je můj výkaz, nebo všichni ve výdělku firmy.' },
  { target: GUIDE.wrPayment, title: 'Proplacení', body: 'Ikona stavu nechá jen vybraný stav platby. Prázdný výběr znamená všechny.' },
  { target: GUIDE.wrMode, title: 'Detailní a souhrn', body: 'Detailní vypíše každý záznam. Souhrn sloučí stejné řádky do jednoho.' },
  filters('Další filtry', 'Pobočka, polygon, stav polygonu a typ objektu jsou další ikony v řadě. Fáze se ukáže, až vyberete typ.'),
  exp('Ikona šipky stáhne řádky, které jsou teď v tabulce.'),
  { target: GUIDE.wrSelect, title: 'Výběr řádků', body: 'Zaškrtnutí v hlavičce vybere všechny řádky filtru. Zaškrtnutí u řádku přidá jen ten.' },
  table('Řádky výkazu', 'Řádek je odvedená práce. Souhrnný řádek slučuje víc záznamů a nemá tužku.'),
  { target: GUIDE.wrRow, title: 'Úprava řádku', body: 'Tužka otevře záznam. Koš ho smaže. U souhrnu tyto ikony nejsou.' },
  pages(),
];

const VYDELEK: PageGuideStep[] = [
  heading('Výdělek firmy', 'Stejný výkaz v cenách smlouvy firmy.'),
  { target: GUIDE.wrBilling, title: 'Platby', body: 'Označit jako vyfakturované zapíše výběr. Nastavit jako neproplacené ho vrátí.' },
  search('Lupa hledá název činnosti, adresu a polygon.'),
  { target: GUIDE.wrDates, title: 'Období', body: 'Kalendář nastaví od a do. Mimo ten rozsah řádky v tabulce nejsou.' },
  { target: GUIDE.wrUser, title: 'Uživatel', body: 'Ikona lidí nechá jen vybraného člověka. Prázdný výběr znamená všechny.' },
  { target: GUIDE.wrPayment, title: 'Proplacení', body: 'Ikona stavu nechá jen vybraný stav platby.' },
  { target: GUIDE.wrMode, title: 'Detailní a souhrn', body: 'Detailní vypíše každý záznam. Souhrn sloučí stejné řádky.' },
  filters('Další filtry', 'Firma, pobočka, polygon, stav polygonu a typ objektu jsou další ikony v řadě.'),
  exp('Ikona šipky stáhne řádky, které jsou teď v tabulce.'),
  { target: GUIDE.wrSelect, title: 'Výběr řádků', body: 'Zaškrtnutí v hlavičce vybere řádky filtru pro hromadnou platbu.' },
  table('Řádky', 'Řádek je práce oceněná podle ceníku firmy.'),
  pages(),
];

const CENIK: PageGuideStep[] = [
  heading('Ceník', 'Ceny prací, podle kterých se počítá výdělek zaměstnance.'),
  search('Lupa najde práci v ceníku podle názvu.'),
  filters('Filtry', 'Ikony vedle lupy zužují položky, pokud je obrazovka má.'),
  exp('Ikona šipky stáhne položky, které jsou teď v tabulce.'),
  table('Položky', 'Řádek je cena práce. Tužka u řádku otevře úpravu, pokud ji smíte.'),
  pages(),
];

const EQUIPMENT: PageGuideStep[] = [
  heading('Zařízení', 'Nářadí a přístroje, sklady a doklady. Pohled přepnete v menu.'),
  actions('Nový doklad', 'Plus založí kategorii, příjem, výdej nebo převod podle otevřené záložky.'),
  search('Lupa najde zařízení nebo doklad podle názvu.'),
  filters('Filtry', 'Ikony vedle lupy zužují sklad, stav nebo člověka.'),
  exp('Ikona šipky stáhne řádky otevřené záložky.'),
  table('Seznam', 'Řádek otevře zařízení nebo doklad.'),
  pages(),
  detail('Detail zařízení', 'Údaje otevřeného kusu, pokud jste v detailu.'),
];

const MATERIALS: PageGuideStep[] = [
  heading('Sklady', 'Zásoby, produkty a doklady. Záložky v menu přepínají zásoby, katalog, příjem a výdej.'),
  { target: GUIDE.whReceipt, title: 'Nová příjemka', body: 'Plus na záložce Příjem založí příjemku na sklad.' },
  { target: GUIDE.whIssue, title: 'Nová výdejka', body: 'Plus na záložce Výdej založí výdejku ze skladu.' },
  { target: GUIDE.whUpload, title: 'Nahrát doklad', body: 'Nahraje sken dokladu k příjmu. Tlačítko je na záložce, která doklady bere.' },
  actions('Export dokladu', 'Tlačítko Export na příjmu nebo výdeji rozbalí PDF a Excel. Na zásobách je export souhrnu.'),
  search('Lupa najde položku, sklad nebo číslo dokladu.'),
  filters('Sklad a filtry', 'Ikony vedle lupy zužují sklad a další sloupce tabulky.'),
  exp('Ikona šipky u tabulky stáhne řádky, které jsou teď vidět.'),
  table('Tabulka', 'Řádky jsou zásoby, produkty nebo doklady. Řádek otevře detail.'),
  pages(),
];

const IMPORT: PageGuideStep[] = [
  heading('Import', 'Nahrání polygonů a podkladů. Krok průvodce importu je na stránce pod nadpisem.'),
  actions('Nový polygon', 'Plus založí polygon, pokud jste na záložce polygonů a máte právo.'),
  search('Lupa zužuje seznam importů nebo polygonů.'),
  filters('Filtry', 'Ikony vedle lupy nechají jen vybraný stav.'),
  exp('Ikona šipky stáhne řádky, které jsou teď v tabulce.'),
  table('Seznam', 'Řádek otevře polygon nebo stav importu.'),
  pages(),
  detail('Průběh', 'Blok s krokem importu nebo výsledkem nahrání.'),
];

const DATABASE: PageGuideStep[] = [
  heading('Databáze', 'Prohlížení tabulek. Odsud se nezapisuje.'),
  { target: GUIDE.dbTables, title: 'Tabulky', body: 'Vlevo je seznam tabulek. Klepnutím načtete její záznamy.' },
  table('Záznamy', 'Vpravo jsou řádky vybrané tabulky.'),
];

const PRIPOJENI: PageGuideStep[] = [
  heading('Připojení', 'Služby, které TelcoTrack používá: Workspace, pošta, datovka, kalendář.'),
  { target: GUIDE.settingsConnections, title: 'Řádky služeb', body: 'Každá služba je jeden řádek. Vpravo je Připojit, Synchronizovat nebo Detaily.' },
];

const VZHLED: PageGuideStep[] = [
  heading('Vzhled', 'Vzhled aplikace pro tento prohlížeč.'),
  { target: GUIDE.settingsTheme, title: 'Motiv', body: 'Tlačítko přepne světlý a tmavý režim. Stejné přepnutí je i v horní liště.' },
  { target: GUIDE.settingsSidebar, title: 'Postranní panel', body: 'Zaškrtnutí nechá menu rozbalené s popisky. Volba zůstane po obnovení stránky.' },
];
